// The file header's Reviewed checkbox, behind the box: the status it shows
// and what ticking or unticking it does (components/FileReviewed.svelte
// shows it; the Files view supplies the open file).
import type { Plan } from '../review/anchors';
import { statusOf, type Status } from '../review/model';

/**
 * A file's review status from its count of lines (`DiffModel.progress`,
 * which the file list's circle and the header read) and its number of
 * change blocks. Unknown until the count is the diff's own (`exact`) and
 * the file's changes are in hand: until then there is nothing to tick.
 */
export function fileStatusOf(progress: { total: number; done: number; exact: boolean }, changes: number): Status {
  return progress.exact && changes > 0 ? statusOf(progress.total, progress.done) : 'unknown';
}

/** The open file's review marks, as the checkbox writes them. */
export interface FileMarks {
  /** The file on screen now; null when none is. */
  file(): string | null;
  /** Review marks cannot be kept here (server data off). */
  off: boolean;
  /** Before an unmark: marks shown but not recorded yet are recorded; false (said to the reader) when they could not be. */
  readyToUnmark(): Promise<boolean>;
  /** The writes that mark or unmark every changed line of `file` (`DiffModel.planFile`); null while its changes are not computed. */
  plan(file: string, reviewed: boolean): Plan | null;
  /** Apply `plan` (the marks, the blocks' folds, the announcement). */
  apply(plan: Plan, reviewed: boolean): void;
}

/**
 * Mark every change of the open file reviewed, or clear its marks. False,
 * with nothing written, when marks are off, an unmark is not ready, the
 * reader moved to another file meanwhile (the plan would be the wrong
 * file's), or the file's changes are not computed.
 */
export async function setFileReviewed(m: FileMarks, reviewed: boolean): Promise<boolean> {
  const file = m.file();
  if (!file || m.off) return false;
  if (!reviewed && !(await m.readyToUnmark())) return false;
  if (m.file() !== file) return false;
  const plan = m.plan(file, reviewed);
  if (!plan) return false;
  m.apply(plan, reviewed);
  return true;
}
